import React from "react";
import { LuExternalLink, LuFlaskConical } from "react-icons/lu";
import { FIELD_HINT, FIELD_LABEL } from "../ui";
import Disclosure from "./Disclosure";
import { CHOICE_FIELDS, ChoiceFieldName, ObjectiveEvidence } from "./calculator";

type Props = {
  name: ChoiceFieldName;
  value: number;
  evidence: ObjectiveEvidence;
  onChange: (name: ChoiceFieldName, value: number) => void;
};

// Outcome assumptions are picked from research-informed scenarios rather than typed, so a visitor
// cannot generate a figure we would not stand behind. The evidence sits beside the choice.
export default function ChoiceField({ name, value, evidence, onChange }: Props) {
  const groupLabelId = `roi-${name}-label`;

  return (
    <div className="tw-min-w-0">
      <p id={groupLabelId} className={FIELD_LABEL}>
        {CHOICE_FIELDS[name].label}
      </p>

      <p className="tw-mt-0 tw-mb-3 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-[#e2f4ed] tw-px-3 tw-py-1.5 tw-text-xs tw-font-bold tw-text-[#0c745c]">
        <LuFlaskConical aria-hidden className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
        Research-informed range
      </p>

      <div
        role="radiogroup"
        aria-labelledby={groupLabelId}
        aria-describedby={`roi-${name}-hint`}
        className="tw-grid tw-grid-cols-3 tw-gap-3 xs:tw-grid-cols-1"
      >
        {evidence.options.map((option) => {
          const checked = value === option.value;

          return (
            <label
              key={option.value}
              htmlFor={`roi-${name}-${option.value}`}
              className={`tw-m-0 tw-flex tw-cursor-pointer tw-flex-col tw-gap-2 tw-rounded-2xl tw-border tw-px-4 tw-py-4 tw-transition-colors focus-within:tw-outline focus-within:tw-outline-2 focus-within:tw-outline-offset-2 focus-within:tw-outline-[#087bd3] ${
                checked
                  ? "tw-border-[#087bd3] tw-bg-[#e9f4fd] tw-shadow-[inset_0_0_0_1px_#087bd3]"
                  : "tw-border-[#c9deed] tw-bg-white hover:tw-bg-[#f4f9fd]"
              }`}
            >
              <span className="tw-flex tw-items-center tw-gap-2.5">
                {/* The control is the dot itself: preflight is off, so the margin reset and the
                    inset ring that draws the selected state both have to be spelled out. */}
                <input
                  id={`roi-${name}-${option.value}`}
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={checked}
                  onChange={() => onChange(name, option.value)}
                  className="tw-m-0 tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-cursor-pointer tw-appearance-none tw-rounded-full tw-border tw-border-[#6b869f] tw-bg-white tw-transition-colors checked:tw-border-[#087bd3] checked:tw-bg-[#087bd3] checked:tw-shadow-[inset_0_0_0_3px_#fff]"
                />
                <span className="tw-text-sm tw-font-bold tw-text-[#173754]">{option.label}</span>
              </span>
              <span className="tw-text-2xl tw-font-medium tw-tracking-[-.03em] tw-text-[#10233f]">
                {option.display}
              </span>
            </label>
          );
        })}
      </div>

      <p id={`roi-${name}-hint`} className={FIELD_HINT}>
        {evidence.hint}
      </p>

      <Disclosure label="Why this range?" variant="panel" className="tw-mt-4">
        <p className="tw-m-0 tw-text-sm tw-leading-6 tw-text-[#5b7188]">{evidence.summary}</p>
        <p className="tw-mt-4 tw-mb-0 tw-text-sm tw-font-bold tw-leading-6 tw-text-[#304d68]">
          {evidence.caveat}
        </p>
        <ul className="tw-mt-4 tw-mb-0 tw-flex tw-list-none tw-flex-wrap tw-gap-x-7 tw-gap-y-2 tw-p-0">
          {evidence.sources.map((source) => (
            <li key={source.url}>
              <a
                href={source.url}
                target="_blank"
                rel="noreferrer"
                className="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-[#087bd3]"
              >
                {source.label}
                <LuExternalLink aria-hidden className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
              </a>
            </li>
          ))}
        </ul>
      </Disclosure>
    </div>
  );
}
